import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { TableActionButton } from '@baseV2'
import { MoreVert, Add, DeleteOutline, Description } from '@material-ui/icons'
import { strings } from '@constants'
import { ActionMenuWrapper, ActionMenuPanel, ActionMenuItem } from './styles'

// Controlled dropdown (open state lives in the parent table) so only one row's
// menu can ever be open at a time — @baseV2's Dropdown manages isOpen internally
// and has no way for a sibling row to force it closed.
const RowActionMenu = React.memo(function RowActionMenu({ item, index, isOpen, onToggle, onClose, onAddGrade, onRemoveGrade, onEditDescription }) {
	const wrapperRef = useRef(null)
	const triggerRef = useRef(null)
	const panelRef = useRef(null)
	const [pos, setPos] = useState(null)

	// Panel is portaled to document.body (see below) so it isn't clipped by the
	// table's scroll container — position it from the trigger button's viewport rect.
	useLayoutEffect(() => {
		if (!isOpen) {
			setPos(null)
			return
		}
		const rect = triggerRef.current?.getBoundingClientRect()
		if (!rect) return
		setPos({ left: rect.right, top: rect.bottom + 4, openUp: false })
	}, [isOpen])

	// Correct for overflow using the panel's actual rendered height (not a guessed
	// constant) so it flips upward only when it truly wouldn't fit below.
	useLayoutEffect(() => {
		if (!isOpen || !pos || pos.openUp || !panelRef.current) return
		const triggerRect = triggerRef.current?.getBoundingClientRect()
		if (!triggerRect) return
		const panelHeight = panelRef.current.getBoundingClientRect().height
		if (window.innerHeight - triggerRect.bottom < panelHeight && triggerRect.top > panelHeight) {
			setPos({ left: triggerRect.right, top: triggerRect.top - 4, openUp: true })
		}
	}, [isOpen, pos])

	useEffect(() => {
		if (!isOpen) return
		const handleOutsideClick = (e) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) onClose()
		}
		const handleKeyDown = (e) => {
			if (e.key === 'Escape') onClose()
		}
		document.addEventListener('mousedown', handleOutsideClick)
		document.addEventListener('keydown', handleKeyDown)
		document.addEventListener('scroll', onClose, true)
		window.addEventListener('resize', onClose)
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick)
			document.removeEventListener('keydown', handleKeyDown)
			document.removeEventListener('scroll', onClose, true)
			window.removeEventListener('resize', onClose)
		}
	}, [isOpen, onClose])

	const hasMessage = Boolean(item?.message)

	return (
		<ActionMenuWrapper ref={wrapperRef}>
			<TableActionButton
				ref={triggerRef}
				type='button'
				aria-haspopup='menu'
				aria-expanded={isOpen}
				aria-label={strings('actions')}
				onClick={onToggle}
			>
				<MoreVert />
			</TableActionButton>
			{isOpen &&
				pos &&
				createPortal(
					<ActionMenuPanel
						ref={panelRef}
						role='menu'
						onMouseDown={(e) => e.stopPropagation()}
						style={{ left: pos.left, top: pos.top, transform: pos.openUp ? 'translate(-100%, -100%)' : 'translateX(-100%)' }}
					>
						{item?.is_added_grade || item?.is_unknown_product ? (
							<ActionMenuItem
								role='menuitem'
								type='button'
								$danger
								onClick={() => {
									onClose()
									onRemoveGrade(index)
								}}
							>
								<DeleteOutline /> {item?.is_unknown_product ? strings('remove_product') : strings('remove_grade')}
							</ActionMenuItem>
						) : (
							<ActionMenuItem
								role='menuitem'
								type='button'
								onClick={() => {
									onClose()
									onAddGrade(index)
								}}
							>
								<Add /> {strings('add_another_grade')}
							</ActionMenuItem>
						)}
						<ActionMenuItem
							role='menuitem'
							type='button'
							onClick={() => {
								onClose()
								onEditDescription(index)
							}}
						>
							<Description /> {hasMessage ? strings('edit_description') : strings('add_description')}
						</ActionMenuItem>
					</ActionMenuPanel>,
					document.body
				)}
		</ActionMenuWrapper>
	)
})

export default RowActionMenu
